import { useEnhancedQuery } from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import React from "react";
import { CourseProgressEnum } from "__generated__/globalTypes";
import Container from "components/common/Container";
import Link from "components/common/Link";
import Loading from "components/common/Loading";
import Page from "components/common/Page";
import { BOARD_APPROVAL_URL, COURSE_URL } from "constants/urls";
import { mapNodes } from "helpers/mapNodes";
import { PageMeta } from "hooks/usePageMeta";
import { RequiredCoursesViewQuery } from "./__generated__/queries";
import { REQUIRED_COURSES_VIEW_QUERY } from "./queries";
import styles from "./RequiredCourses.module.css";

const RequiredCourses = () => {
  const { data, loading } = useEnhancedQuery<RequiredCoursesViewQuery>(REQUIRED_COURSES_VIEW_QUERY);
  if (loading || !data) <Loading />;
  return (
    <>
      <div className={styles.tableOfContentsBox}>
        <h2 className={styles.tableOfContentsBoxTitle}>Table of Contents</h2>
        <div className={styles.tableOfContents}>
          {mapNodes(data?.fulfillsRequirements, requirement => (
            <a
              key={requirement.id}
              className={styles.tableOfContentsLink}
              href={`#${requirement.slug}`}
            >
              {requirement.name}
            </a>
          ))}
        </div>
      </div>
      <div className={styles.boardNotes}>
        <p>
          NOTE: Individual Board Rules are subject to change. Each local and National professional
          board has its own specific Rules and Regulations for Continuing Education Credit Approval
          and Acceptance. The approval and acceptance information we provide is believed to be
          accurate at the time of printing. Check with your State Regulatory board if you have
          questions or concerns not clarified by the information provided below. Ultimately you are
          responsible for the acceptance or denial of your license renewal credit hours.{" "}
          <Link href={BOARD_APPROVAL_URL}>
            See details of Zur Institute&apos;s CE board approvals
          </Link>
        </p>
      </div>
      {mapNodes(data?.fulfillsRequirements, requirement => (
        <div key={requirement.id} id={requirement.slug || ""} className={styles.requirementBox}>
          <h2
            className={styles.requirementBoxTitle}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: requirement.description || requirement.name || "" }}
          />
          <div className={styles.requirementCourses}>
            {mapNodes(requirement.courses, course => (
              <Link
                key={course.id}
                className={styles.requirementLink}
                href={makeUrl(COURSE_URL, {
                  slug: course.slug as string,
                  page: CourseProgressEnum.OVERVIEW.toLocaleLowerCase(),
                })}
              >
                {course.title}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </>
  );
};

interface RequiredCoursesPageProps {
  title: string;
  seo?: PageMeta | null;
}

const RequiredCoursesPage = ({ title, seo }: RequiredCoursesPageProps) => {
  return (
    <Container>
      <Page title={title} seo={seo}>
        <RequiredCourses />
      </Page>
    </Container>
  );
};

export default RequiredCoursesPage;
